/* eslint-disable no-console */

import Boom from 'boom'
import { compact, flatten, intersection, pick } from 'lodash'
import { inspect } from 'lib/inspect'

/**
 * Function type for colorizing strings
 */
type colorful = (str: string) => string
let colors: {
  magenta: colorful
  red: colorful
  cyan: colorful
  yellow: colorful
}
try {
  colors = require(`colors/safe`)
} catch {
  colors = {
    magenta: (str) => str,
    red: (str) => str,
    cyan: (str) => str,
    yellow: (str) => str,
  }
}

let rtracer: { id: () => string } | null = null
try {
  rtracer = require(`cls-rtracer`)
} catch (e) {
  //
}

/**
 * Logs a message with timestamp and optional trace ID
 * @param {...unknown[]} args - Arguments to log
 * @returns {void}
 */
export function log(...args: unknown[]): void {
  let id = rtracer?.id()
  if (id) {
    console.log(new Date().toISOString(), `trace:${id}`, ...args)
  } else {
    console.log(new Date().toISOString(), ...args)
  }
}

/**
 * Logs debug information if the associated debug tags are enabled
 * @param {string | string[]} tags - Debug tags to check against DEBUG_TAGS environment variable
 * @param {string} message - The primary debug message
 * @param {...unknown[]} args - Additional arguments to log
 * @returns {void}
 * @description Only logs when the specified tags match those in DEBUG_TAGS environment variable
 */
export function debug(tags: string | string[], message: string, ...args: unknown[]): void {
  if (tagsActive(tags)) {
    log(colors.cyan(`[${tags}]`), message, ...args)
  }
}

/**
 * Logs a stack trace if the associated debug tags are enabled
 * @param {string | string[]} tags - Debug tags to check against DEBUG_TAGS environment variable
 * @param {string} message - The trace message
 * @param {...unknown[]} args - Additional arguments to log
 * @returns {void}
 */
export function trace(tags: string | string[], message: string, ...args: unknown[]): void {
  if (tagsActive(tags)) {
    console.trace(`[${tags}]`, message, ...args)
  }
}

/**
 * Logs an error message with special handling for Axios and Boom errors
 * @param {string} message - Error description
 * @param {...any[]} args - Additional arguments, typically error objects
 * @returns {void}
 * @description Formats Axios errors and Boom errors for better readability
 */
export function logError(message: string, ...args: any[]): void {
  let id = rtracer?.id()
  args = args.map((arg) => {
    if (arg?.isAxiosError) {
      return inspect(
        pick(
          arg,
          `message`,
          `name`,
          `code`,
          `status`,
          `config.url`,
          `config.method`,
          `config.data`,
          `config.headers`,
          `response.data`,
          `stack`,
        ),
      )
    }
    if (Boom.isBoom(arg)) {
      return `- BoomError: ${inspect(arg.output, false)}-${arg.message}-${arg.stack}`
    }
    return arg
  })
  if (id) {
    console.error(new Date(), colors.red(`[error]`), `trace:${id}`, colors.red(message) || message, ...args)
  } else {
    console.error(new Date(), colors.red(`[error]`), colors.red(message) || message, ...args)
  }
}

/**
 * Logs a warning message with yellow coloring
 * @param {string} message - Warning message
 * @param {...any[]} args - Additional arguments to log
 * @returns {void}
 */
export function logWarn(message: string, ...args: any[]): void {
  let id = rtracer?.id()
  if (id) {
    console.warn(new Date(), colors.yellow(`[warning]`), `trace:${id}`, colors.yellow(message) || message, ...args)
  } else {
    console.warn(new Date(), colors.yellow(`[warning]`), colors.yellow(message) || message, ...args)
  }
}

/**
 * Checks if the given debug tags are currently active
 * @param {string | string[]} tags - Tags to check
 * @returns {boolean} True if any of the tags match DEBUG_TAGS environment variable
 * @description Supports DEBUG_TAGS=true for all tags, or comma-separated list of specific tags
 */
export function tagsActive(tags: string | string[]): boolean {
  let activeTags: any = process.env.DEBUG_TAGS

  if (!activeTags) {
    return false
  }
  if (activeTags !== `true`) {
    activeTags = compact(activeTags.split(/,/).map((t) => t.trim()))
    if (intersection(activeTags, flatten([tags])).length === 0) {
      return false
    }
  }
  return true
}
